import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Heading – reimagined semantic heading with decoupled visual size
// ---------------------------------------------------------------------------

/**
 * Heading level — determines the HTML element (h1–h6) for semantics/a11y.
 */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Visual size — controls font size independently of semantic level.
 * When omitted, auto-maps from level.
 */
export type HeadingSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';

interface HeadingSignature {
  Args: {
    /** Semantic heading level (h1–h6). Default: 2 */
    level?: HeadingLevel;
    /** Visual font size. Auto-maps from level when omitted. */
    size?: HeadingSize;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLHeadingElement;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------

/** Auto-size when @size is not provided */
const LEVEL_TO_SIZE: Record<HeadingLevel, HeadingSize> = {
  1: '3xl',
  2: '2xl',
  3: 'xl',
  4: 'lg',
  5: 'base',
  6: 'sm',
};

const SIZE_CLASSES: Record<HeadingSize, string> = {
  '3xl': 'text-3xl',
  '2xl': 'text-2xl',
  xl: 'text-xl',
  lg: 'text-lg',
  base: 'text-base',
  sm: 'text-sm',
  xs: 'text-xs',
};

/**
 * Semantic heading with decoupled visual sizing.
 *
 * The `@level` determines the HTML element (`h1`–`h6`) for accessibility
 * and document outline. The `@size` controls the visual font size
 * independently, so you can use `h2` semantics with `text-lg` display.
 *
 * @example Auto-sized from level
 * ```gts
 * import Heading from 'tio-ui/components/heading';
 *
 * <Heading @level={{1}}>Page Title</Heading>
 * <Heading @level={{3}}>Section Title</Heading>
 * ```
 *
 * @example Explicit visual size
 * ```gts
 * <Heading @level={{2}} @size="lg">Smaller h2</Heading>
 * ```
 */
export default class Heading extends Component<HeadingSignature> {
  get level() {
    return this.args.level ?? 2;
  }

  get sizeClass() {
    const size = this.args.size ?? LEVEL_TO_SIZE[this.level];
    return SIZE_CLASSES[size];
  }

  get tag() {
    return `h${this.level}` as const;
  }

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag data-test-heading class="app-heading {{this.sizeClass}}" ...attributes>{{yield}}</Tag>
    {{/let}}
  </template>
}

export type { HeadingSignature };
